import { FETCH_LIMIT } from '@constants/limits';
import { remove, send } from './client';
import type { CylinderStatus, FillState, LocationType, PositionStatus, RejectReason, SessionKind, ShipmentKind, ShipmentStatus, WithdrawalReason } from './common';
import { query, type BoundedTotal, type Cursor, type CursorPage } from './paging';

// Shipments are Surat Jalan (delivery notes): loaded at the origin, departed, unloaded and received at the destination.

/** One delivery note with its route and counters (the backend's ShipmentSummary: list, detail, SSE shipment.updated). */
export interface ShipmentSummary {
  id: number;
  number: string | null;
  status: ShipmentStatus;
  originLocationId: number;
  destinationLocationId: number;
  vehiclePlate: string;
  driverName: string;
  note: string | null;
  isDemo: boolean;
  /** origin and destination are not in one parent/child line of the network (absent from an older backend) */
  offNetwork?: boolean;
  createdAt: string;
  createdBy: number | null;
  departedAt: string | null;
  departedBy: number | null;
  unloadStartedAt: string | null;
  unloadStartedBy: number | null;
  receivedAt: string | null;
  receivedBy: number | null;
  cancelledAt: string | null;
  cancelledBy: number | null;
  updatedAt: string;
  originName: string;
  originType: LocationType;
  originRegencyCode: string;
  originProvinceCode: string;
  destinationName: string;
  destinationType: LocationType;
  destinationRegencyCode: string;
  destinationProvinceCode: string;
  itemCount: number;
  receivedCount: number;
  missingCount: number;
  extraCount: number;
  rejectedCount: number;
  /** a scan session is open for it right now */
  scanning: boolean;
  /** the regency (kabupaten/kota) of each end, by name (newer backends) */
  originRegencyName?: string | null;
  destinationRegencyName?: string | null;
  /**
   * In transit: when it is expected to arrive (frozen at departure: the route's usual duration, else distance ÷ speed +
   * handling, the same estimate as the Ruang Kendali trucks); the share of the trip done as of the answer (0..1); and
   * whether the expected arrival has passed (the Terlambat tab). Absent from an older backend, null without an estimate.
   */
  expectedArrivalAt?: string | null;
  progress?: number | null;
  late?: boolean;
  /** received with cylinders missing, refused while loading or not on the manifest (the Ada selisih tab) */
  hasDiscrepancy?: boolean;
  /** along the chain, or a withdrawal to a Bengkel Uji (fixed at creation); absent from an older backend = DISTRIBUTION */
  kind?: ShipmentKind;
  /** a withdrawal's default reason for its cylinders; null for a distribution note */
  withdrawalReason?: WithdrawalReason | null;
}

/** How a loaded cylinder ended on this note: received or did not arrive; null while the note is open. */
export type ShipmentItemOutcome = 'RECEIVED' | 'MISSING';
/** GET /shipments/:id/items?outcome=: a stored outcome, or PENDING (not received yet). */
export const ITEM_OUTCOME_FILTERS = ['RECEIVED', 'MISSING', 'PENDING'] as const;
export type ItemOutcomeFilter = (typeof ITEM_OUTCOME_FILTERS)[number];

export interface ShipmentItem {
  cylinderId: number;
  serialNo: string;
  /** the maker's serial stamped on the body, when known (compared with the label at a shortfall) */
  manufacturerSerial?: string | null;
  epc: string;
  kind: string;
  capacity: string | null;
  /** YYYY-MM-DD */
  nextTestDue: string | null;
  isDemo: boolean;
  cylinderStatus: CylinderStatus;
  loadedAt: string;
  manual: boolean;
  receivedAt: string | null;
  receivedManual: boolean;
  outcome: ShipmentItemOutcome | null;
  /** a withdrawn cylinder's reason (null on a distribution note; absent from an older backend) */
  withdrawalReason?: WithdrawalReason | null;
  positionStatus: PositionStatus;
  /** what it holds now (absent from an older backend) */
  fillState?: FillState;
  lastSeenAt: string | null;
  lastSeenLocationName: string | null;
}
/** A tag read while unloading that was not on the manifest. */
export interface ShipmentExtra { epc: string; cylinderId: number | null; serialNo: string | null; seenAt: string; recordedLocationName: string | null }
/** A tag read while loading that was refused, with why. */
export interface ShipmentRejection { epc: string; cylinderId: number | null; serialNo: string | null; reason: RejectReason; message: string | null; at: string }
export type TimelineType = 'created' | 'loaded' | 'departed' | 'unloaded' | 'received' | 'received_with_discrepancy' | 'cancelled';
export interface TimelineEntry {
  type: TimelineType; at?: string | null; by?: string | null; from?: string | null; to?: string | null; count?: number; rejected?: number; manual?: number;
  matched?: number; extra?: number; missing?: number;
}
/** Who signs the printed delivery note, and when. */
export interface ShipmentSignatures { loadedBy: string | null; loadedAt: string | null; driver: string; driverAt: string | null; receivedBy: string | null; receivedAt: string | null }

export interface LatLng { lat: number; lng: number }
/** A Reader the note was loaded (`load`) or unloaded (`unload`) with. */
export interface ShipmentReader { kind: SessionKind; gwId: string; label: string }

/** GET /shipments/:id: the manifest in full up to 2,000 cylinders (`itemCount` is the real size). */
export interface ShipmentDetail extends ShipmentSummary {
  signatures: ShipmentSignatures;
  items: ShipmentItem[];
  extras: ShipmentExtra[];
  rejections: ShipmentRejection[];
  session: { id: number; kind: SessionKind; startedAt: string } | null;
  timeline: TimelineEntry[];
  /** where its ends are, for the route map (null: a place without coordinates; newer backends) */
  route?: { origin: LatLng | null; destination: LatLng | null };
  /** the Readers it was loaded and unloaded with (newer backends) */
  readers?: ShipmentReader[];
}

/** GET /shipments/:id/items: the manifest a page at a time (keyset by cylinder id); `outcome` narrows it. */
export type ShipmentItemPage = CursorPage<ShipmentItem & { id: number }>;
export interface ShipmentItemFilter { outcome?: ItemOutcomeFilter | '' }

/** The list's status tabs: Terlambat = in transit past the expected arrival; Ada selisih = received with a discrepancy. */
export const SHIPMENT_TABS = ['all', 'loading', 'in_transit', 'late', 'discrepancy', 'done', 'cancelled'] as const;
export type ShipmentTab = (typeof SHIPMENT_TABS)[number];
/** GET /shipments/tabs: each tab's count over the list's filters (exact up to a cap: `exact` false = "more than"). */
export type ShipmentTabCounts = (BoundedTotal & { tab: ShipmentTab })[];

/** Which end of a note a chosen Titik Distribusi must be (`side`). */
export const SHIPMENT_SIDES = ['both', 'origin', 'destination'] as const;
export type ShipmentSide = (typeof SHIPMENT_SIDES)[number];

/** One party of a printed delivery note. */
export interface PrintParty { id: number; code: string; name: string; address: string | null; regencyName: string; provinceName: string }
/** GET /shipments/:id/print: what the printed document shows, header to signatures. */
export interface ShipmentPrint {
  org: { name: string; address: string };
  id: number;
  /** RE-CHECK against the final backend: the print view does not carry the kind yet (WIP); absent = a plain title */
  kind?: ShipmentKind;
  number: string | null;
  status: ShipmentStatus;
  isDemo: boolean;
  vehiclePlate: string;
  driverName: string;
  note: string | null;
  createdAt: string;
  departedAt: string | null;
  receivedAt: string | null;
  origin: PrintParty;
  destination: PrintParty;
  itemCount: number;
  /** more cylinders than the document lists: `items` is not the whole manifest */
  itemsTruncated: boolean;
  items: { serialNo: string; kind: string; capacity: string | null; nextTestDue: string | null; outcome: ShipmentItemOutcome | null; loadedAt: string; receivedAt: string | null; /** RE-CHECK: a withdrawn cylinder's reason */ withdrawalReason?: WithdrawalReason | null }[];
  signatures: ShipmentSignatures;
  /** the QR's verification: the token and the public page it opens (newer backends) */
  verify?: { token: string; url: string };
}

/**
 * GET /verify/sj/:token (public): only non-personal facts that the printed document is genuine, the same keys whether
 * the token is known or not (`found` false: every fact null). No person, no plate, no id.
 */
export interface ShipmentVerification {
  found: boolean;
  /** the server's sentence ("Dokumen ini terdaftar di sistem", …) */
  statement: string;
  number: string | null;
  status: ShipmentStatus | null;
  isDemo: boolean | null;
  originName: string | null;
  destinationName: string | null;
  createdAt: string | null;
  departedAt: string | null;
  receivedAt: string | null;
  cancelledAt: string | null;
  cylinderCount: number | null;
}

/** GET /shipments/last-vehicle: the plate and driver remembered for a location. */
export interface LastVehicle { vehiclePlate: string; driverName: string; createdAt: string }

/** GET /shipments filters; `from`/`to` are WIB days of creation, both inclusive. `total` is not counted with `q`. */
export interface ShipmentFilter {
  status?: ShipmentStatus | '';
  /** a status tab (newer backends); `status` stays for the operations screens */
  tab?: ShipmentTab | '';
  locationId?: number;
  /** with `locationId`: it is the origin, the destination, or either end (the default) */
  side?: ShipmentSide | '';
  provinceCode?: string;
  regencyCode?: string;
  /** from or to a location in this district (kecamatan) / village (desa / kelurahan) */
  districtCode?: string;
  villageCode?: string;
  /** distribution notes or withdrawals */
  kind?: ShipmentKind | '';
  from?: string;
  to?: string;
  /** the start of a note number or a plate (the driver's name only for a caller who may read it) */
  q?: string;
}
/** GET /shipments/tabs: the list's filters without the tab (the search narrows the counts too). */
export type ShipmentCountFilter = Omit<ShipmentFilter, 'tab' | 'status'>;
/** Newest first (by creation). */
export type ShipmentPage = CursorPage<ShipmentSummary>;

type Id = number | string | undefined;

export const shipmentPaths = {
  list: (f: ShipmentFilter, cursor: Cursor, limit: number) => `/shipments${query({ ...f, ...cursor, limit })}`,
  tabs: (f: ShipmentCountFilter) => `/shipments/tabs${query({ ...f })}`,
  detail: (id: Id) => `/shipments/${id}`,
  items: (id: Id, f: ShipmentItemFilter, cursor: Cursor, limit: number) => `/shipments/${id}/items${query({ ...f, ...cursor, limit })}`,
  print: (id: Id) => `/shipments/${id}/print`,
  /** public: no session needed */
  verify: (token: string) => `/verify/sj/${encodeURIComponent(token)}`,
  /** in transit towards a location */
  incoming: (locationId: number) => `/shipments/incoming${query({ locationId })}`,
  /** plate and driver of the last shipment from a location ("terakhir diingat") */
  lastVehicle: (locationId: number) => `/shipments/last-vehicle${query({ locationId })}`,
  /** loads at a location that have not departed yet */
  loadingAt: (locationId: number) => shipmentPaths.list({ status: 'LOADING', locationId }, {}, FETCH_LIMIT.loadingAtLocation),
  /** shipments from or to a location created on one WIB day */
  onDay: (locationId: number, day: string) => shipmentPaths.list({ locationId, from: day, to: day }, {}, FETCH_LIMIT.locationDay),
  recentAt: (locationId: number) => shipmentPaths.list({ locationId }, {}, FETCH_LIMIT.locationShipments),
};

/** Steps of the load and unload flow that take no body. */
export type ShipmentStep = 'pause' | 'resume' | 'depart' | 'cancel' | 'receive/start';

/** 201 with the new delivery note, loading at its origin right away (an operator's own location when left out). */
export interface CreateShipmentInput {
  originLocationId?: number;
  destinationLocationId: number;
  vehiclePlate: string;
  driverName: string;
  /** DISTRIBUTION (the default) or WITHDRAWAL; the route must fit it */
  kind?: ShipmentKind;
  /** a withdrawal's default reason for its cylinders (required for a withdrawal, refused on a distribution note) */
  withdrawalReason?: WithdrawalReason;
}
export const createShipment = (body: CreateShipmentInput) => send<ShipmentDetail>('/shipments', body);
export const shipmentStep = (id: number, step: ShipmentStep) => send<ShipmentDetail>(`/shipments/${id}/${step}`);
/** Load (or, while unloading, confirm) a cylinder by its printed serial number. */
export const addShipmentItem = (id: number, serialNo: string) => send<ShipmentDetail>(`/shipments/${id}/items`, { serialNo });
export const removeShipmentItem = (id: number, cylinderId: number) => remove<ShipmentDetail>(`/shipments/${id}/items/${cylinderId}`);
/** A withdrawn cylinder's own reason, while the withdrawal is loading at its origin; the same reason again changes nothing. */
export const setShipmentItemReason = (id: number, cylinderId: number, withdrawalReason: WithdrawalReason) => send<ShipmentDetail>(`/shipments/${id}/items/${cylinderId}`, { withdrawalReason }, 'PATCH');
export const finishReceive = (id: number, note: string | null) => send<ShipmentDetail>(`/shipments/${id}/receive/finish`, { note });
